import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { motion } from "framer-motion";
import * as actions from '../redux/actions/billing';
import { ArrowLeft, Check, Zap } from "lucide-react";
import { HomepageLoader } from "../components/Homepage/HomepageLoader";
import { PricingPlanModal } from "../components/PricingPlanModal";
import { useNavigate } from "react-router-dom";
import { generateMetadata } from "../seo/metadataGenerator";
import { isPrerendering } from "../helper/isPrerendering";
import { PAGE_OG_IMAGES, SITE_URL } from "../seo/constants";
import { generateBreadcrumbSchema, generateGraphSchema, generateProductSchema, generateSoftwareApplicationSchema } from "../seo/schemaGenerator";
import SeoHead from "../seo/SeoHead";
import { usePrerenderReady } from "../hooks/usePrerenderReady";

export default function PricingPage() {
  usePrerenderReady();
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isAnnual, setIsAnnual] = useState(false);
  const [isLoad, setIsLoad] = useState(false)
  const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Updated to match new state structure
  const { hasActiveSubscription, subscriptionPlan } = useSelector((state) => state.billing);

  // Extract nested data safely
  const planList = subscriptionPlan || [];
  const monthlyPlan = planList.find(item => item.interval === "monthly") || null;
  const yearlyPlan = planList.find(item => item.interval === "yearly") || null;
  const lastSubscriptionFetched = subscriptionPlan?.lastFetched;
  const token = localStorage.getItem("authToken");

  const updateCursorPosition = useCallback((e) => {
    requestAnimationFrame(() => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    });
  }, []);

  useEffect(() => {
    document.body.style.cursor = "none";
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const interactiveElements = document.querySelectorAll(
      'a, button, input, [role="button"]'
    );
    interactiveElements.forEach((element) => {
      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);
    });

    window.addEventListener("mousemove", updateCursorPosition);

    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
      document.body.style.cursor = "auto";
      window.removeEventListener("mousemove", updateCursorPosition);
      interactiveElements.forEach((element) => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      });
    };
  }, [updateCursorPosition]);

  const hasFetched = useRef(false);
  useEffect(() => {
    const fetchPlans = async () => {
      const shouldFetch =
        !planList?.length ||
        !lastSubscriptionFetched ||
        Date.now() - lastSubscriptionFetched > 1000 * 60 * 30; // 30 mins cache

      if (shouldFetch && !hasFetched.current && !isPrerendering()) {
        hasFetched.current = true;
        await dispatch(actions.getSubscriptionPlans());
      }
    };

    fetchPlans();

    return () => {
      hasFetched.current = false;
    };
  }, [dispatch, planList?.length, lastSubscriptionFetched]);

  const handleSubscription = async () => {
    if (token) {
      try {
        setIsLoad(true);
        if (hasActiveSubscription) {
          await dispatch(actions.getCustomerPortalSession());
        } else {
          setShowPricingPlanModal(true);
        }
      } catch (err) {
        console.error('Error handling Pro button:', err);
      } finally {
        setIsLoad(false);
      }
    } else {
      navigate('/signup')
    }
  }

  //SEO
  const metadata = generateMetadata({
    title:
      "Subscription Pricing & Plans | UpNComer",

    description:
      "Explore UpNComer pricing plans for music distribution, AI marketing, analytics, mastering and artist growth tools built for independent musicians.",

    image: PAGE_OG_IMAGES.pricing,

    path: "/pricing",
  })

  const pricingSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "UpNComer Monthly Plan",
      description:
        "Monthly subscription plan for independent artists including AI music marketing, music distribution, analytics, mastering, fan engagement and artist growth tools.",
      offers: {
        "@type": "Offer",
        price: "27",
        priceCurrency: "USD",
        availability:
          "https://schema.org/InStock",
        url:
          `${SITE_URL}/pricing`,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "UpNComer Yearly Plan",
      description:
        "Annual subscription plan for independent artists including AI music marketing, music distribution, analytics, mastering, fan engagement and artist growth tools.",
      offers: {
        "@type": "Offer",
        price: "300",
        priceCurrency: "USD",
        availability:
          "https://schema.org/InStock",
        url:
          `${SITE_URL}/pricing`,
      },
    },
  ].map((schema) => (
    generateProductSchema({
      name: schema.name,
      description: schema.description,
      price: schema.offers.price,
      currency: schema.offers.priceCurrency,
      url: schema.offers.url
    })
  ))

  const breadcrumbSchema = generateBreadcrumbSchema([
    {
      "name": "Home",
      "path": "/"
    },
    {
      "name": "Pricing",
      "path": "/pricing"
    }
  ])

  const schemas = generateGraphSchema([
    pricingSchemas,
    generateSoftwareApplicationSchema(),
    breadcrumbSchema
  ])

  return (
    <>
      {/* {isLoading && <HomepageLoader />} */}
      <SeoHead
        metadata={metadata}
        schemas={schemas}
      />
      <div
        className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`,
          transform: `translate(-50%, -50%)`,
        }}
      >
        <div className="inner-circle"></div>
      </div>
      {/* <div
        className={`transition-all duration-1000 ${isLoading ? "opacity-0 blur-xl" : "opacity-100 blur-none"
          }`}
      > */}
      <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] to-[#1e3a8a] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] to-[#3b82f6] opacity-90" />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
          />
        </div>

        <main className="relative z-10 px-8 pt-20 mt-20 pb-20">
          <div className="container mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h1 className="text-5xl font-bold text-white mb-4">
                🔥 Replace a $10K/month team — for just{" "}
                <span className="text-[#FA6138]">${monthlyPlan?.price}</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
                Get the power of a full label team while keeping 100% creative and financial control.
              </p>

              {/* Billing Toggle */}
              <div className="flex items-center justify-center space-x-4 mb-8">
                <span
                  className={`text-sm ${!isAnnual ? "text-white" : "text-gray-400"
                    }`}
                >
                  Monthly
                </span>
                <button
                  onClick={() => setIsAnnual(!isAnnual)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isAnnual ? "bg-[#FA6138]" : "bg-gray-600"
                    }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isAnnual ? "translate-x-6" : "translate-x-1"
                      }`}
                  />
                </button>
                <span
                  className={`text-sm ${isAnnual ? "text-white" : "text-gray-400"
                    }`}
                >
                  Annual
                </span>
              </div>

              {/* Free Campaign Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-flex items-center bg-green-500/20 border border-green-500/30 text-green-400 px-4 py-2 rounded-full text-sm mb-12"
              >
                <Zap className="w-4 h-4 mr-2" />
                Create your account — no credit card required
              </motion.div>
            </motion.div>

            <div className="flex justify-center items-center min-h-screen px-4 gap-8 mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative bg-white/5 backdrop-blur-sm border rounded-xl p-8 border-[#FA6138]/50 shadow-[0_0_30px_rgba(250,97,56,0.2)] hover:border-[#FA6138]/50 transition-all duration-300 max-w-2xl w-full"
              >
                {/* Badge */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-[#FA6138] text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </span>
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-white mb-2">🟧 UpNComer Pro</h3>
                  <p className="text-gray-300 mb-4">For serious artists scaling their career</p>

                  {/* Pricing */}
                  <div className="space-y-1">
                    <div className="text-4xl font-bold text-[#FA6138]">{isAnnual ? `$${yearlyPlan?.price}` : `$${monthlyPlan?.price}`}<span className="text-xl text-gray-300 font-normal"> / {isAnnual ? "year" : "month"}</span>{isAnnual && <span className="text-xl font-normal text-[#FA6138]"> (Save 2 months)</span>}</div>
                    {/* <div className="text-sm text-green-400">🆓 Create your account — no credit card required</div> */}
                  </div>
                  {/* CTA Button */}
                  <button
                    onClick={handleSubscription}
                    className="inline-block w-full py-4 px-6 mt-8 rounded-lg font-bold text-lg text-white bg-[#FA6138] hover:bg-[#e55532] shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    BECOME AN UPNCOMER →
                  </button>
                </div>

                {/* Features */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-white mb-4">🎯 Includes:</h4>
                  <ul className="space-y-3">
                    {[
                      "Growth Engine — Run smart, goal-driven ad campaigns",
                      "UrStudio — AI-powered post-production suite for mixing, mastering, and audio optimization",
                      "Amplitude AI — Predictive intelligence across your career",
                      "Data Analyst — Streaming & audience analytics",
                      "Artist Manager — Contracts, revenues, smart links",
                      "Publicist (coming soon) — Press coverage & label tracking",
                      "ProfessorU — Industry education & trend spotting",
                      "Content Creator (coming soon) — Create & schedule content",
                      "Support — Live chat & email",
                      "1 Artist Profile — All tools apply to a single artist account",
                    ].map((feature, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-[#FA6138] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Bottom Section */}
                <div className="text-center space-y-3">
                  <p className="text-gray-300">
                    <span className="font-semibold text-white">🔶 Become an UpNComer</span><br />
                    Everything you need to scale — for less than the cost of {isAnnual ? "a studio session." : "your t-shirt."}
                  </p>
                  {/* <p className="text-gray-400 text-sm">
                      $25/month • $250/year <br />🆓 <i>No credit card required • 🎁 Start free today</i>
                    </p> */}
                </div>
              </motion.div>
            </div>


            {/* Value Comparison Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="bg-white/5 backdrop-blur-sm border border-[#FA6138]/20 rounded-xl p-8 mb-16"
            >
              <h2 className="text-3xl font-bold text-white text-center mb-8">
                📊 Why Artists Choose UpNComer Over Traditional Services
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#FA6138]/20">
                      <th className="text-left py-4 px-4 text-white">
                        Service
                      </th>
                      <th className="text-center py-4 px-4 text-gray-300">
                        Traditional Cost
                      </th>
                      <th className="text-center py-4 px-4 text-[#FA6138]">
                        UpNComer Professional
                      </th>
                      <th className="text-center py-4 px-4 text-green-400">
                        You Save
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {[
                      [
                        "Ads Agency (Meta, TikTok, YouTube)",
                        "$3,000",
                        "Included",
                        "$3,000",
                      ],
                      [
                        "Audio Engineer (Mastering & Revisions)",
                        "$2,000",
                        "Included",
                        "$2,000",
                      ],
                      [
                        "Data Analytics Tools & Consultants",
                        "$1,000",
                        "Included",
                        "$1,000",
                      ],
                      [
                        "PR Firm (Press, Playlist & Sync)",
                        "$1,500",
                        "Coming Soon",
                        "$1,500",
                      ],
                      [
                        "Artist Management / Consultant",
                        "$1,000",
                        "Included",
                        "$1,000",
                      ],
                      [
                        "Accounting / Revenue Tracking Tools",
                        "$500",
                        "Included",
                        "$500",
                      ],
                      [
                        "Smart Links & Fan CRM Tools",
                        "$500",
                        "Included",
                        "$500",
                      ],
                    ].map(
                      ([service, traditional, upncomer, savings], index) => (
                        <tr
                          key={index}
                          className="border-b border-[#FA6138]/10"
                        >
                          <td className="py-3 px-4 text-white">{service}</td>
                          <td className="py-3 px-4 text-center text-gray-300">
                            {traditional}
                          </td>
                          <td className="py-3 px-4 text-center text-[#FA6138] font-medium">
                            ✅ {upncomer}
                          </td>
                          <td className="py-3 px-4 text-center text-green-400 font-medium">
                            {savings}
                          </td>
                        </tr>
                      )
                    )}
                    <tr className="border-t-2 border-[#FA6138]/30 font-bold">
                      <td className="py-4 px-4 text-white text-lg">
                        Total Monthly Cost
                      </td>
                      <td className="py-4 px-4 text-center text-gray-300 text-lg">
                        $10,000+
                      </td>
                      <td className="py-4 px-4 text-center text-[#FA6138] text-lg">
                        ${monthlyPlan?.price}/month or ${yearlyPlan?.price}/year
                      </td>
                      <td className="py-4 px-4 text-center text-green-400 text-lg">
                        $$$$
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-8">
                <h4 className="text-lg font-semibold text-center text-white mb-4">
                  🧠 Notes & Perks
                </h4>
                <ul className="space-y-4">
                  {[
                    {
                      title: "🎧 Mastering and Mixing are deeply discounted at $4.00 or $5.00 per track, respectively.",
                      sub: "Normally $250–$1000+ per track with revisions",
                    },
                    {
                      title:
                        "🧠 Amplitude AI includes 50 credits/month, trained on your data, campaigns, and brand with top-ups available",
                    },
                    {
                      title:
                        "🔄 Publicist and Content Creator will be included in Pro — no extra cost when they launch",
                    },
                  ].map((item, i) => (
                    <li key={i} className="pl-4 relative">
                      {/* dot before each line */}
                      <span className="absolute left-0 top-0 text-[#FA6138]"><Check className="w-5 h-5 text-[#FA6138] flex-shrink-0 mt-0.5" /></span>
                      <div className="text-gray-300 text-sm leading-relaxed ml-4">
                        {item.title}
                        {item.sub && (
                          <div className="text-gray-400 text-xs mt-1">{item.sub}</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* FAQ Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-center"
            >
              <h2 className="text-3xl font-bold text-white mb-8">
                Frequently Asked Questions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {[
                  {
                    question: "Can I start for free?",
                    answer:
                      "Yes — you can create your account and explore the platform with no credit card required.",
                  },
                  {
                    question: "Do I need a subscription to access features?",
                    answer:
                      "Yes — UpNComer Pro gives you full access to all modules and AI tools. Without it, your account stays in preview mode.",
                  },
                  {
                    question: "Do I keep 100% of my royalties?",
                    answer:
                      "Absolutely. You own everything — streaming revenue, publishing rights, fan data.",
                  },
                  {
                    question: "How does Amplitude AI work?",
                    answer:
                      "Amplitude AI is your personal strategy engine — trained on your data, campaigns, fanbase behavior, and global trends. It helps guide your next move — and adapts as you grow.",
                  },
                  {
                    question: "What if I don’t see results?",
                    answer:
                      "We’re confident you will. But if you ever feel stuck, our team offers hands-on support to get you back on track.",
                  },
                ].map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white/5 backdrop-blur-sm border border-[#FA6138]/20 rounded-lg p-6 text-left"
                  >
                    <h3 className="text-white font-semibold mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-gray-300 text-sm">{faq.answer}</p>
                  </div>
                ))}
              </div>

              <div className="text-center space-y-3 mt-12">
                <p className="text-gray-300">
                  <span className="font-semibold text-white text-3xl">🚀 Become an UpNComer Today</span>
                </p>
                <p className="text-gray-400 text-sm">
                  ${monthlyPlan?.price}/month • ${yearlyPlan?.price}/year <br />🆓 <i>No credit card required • 🎁 Start free today</i>
                </p>
                <br />
                {/* CTA Button */}
                <button
                  onClick={handleSubscription}
                  className="inline-block w-[32vw] py-2 px-4 rounded-lg font-bold text-lg text-white bg-[#FA6138] hover:bg-[#e55532] shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  JOIN NOW
                </button>
              </div>
              {/* Additional gradient overlay for smoother transition */}
              <div
                className="absolute left-0 right-0 bottom-0 h-32 z-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(to bottom, transparent, rgba(0, 6, 126, 0.3) 50%, rgba(0, 4, 86, 0.6) 100%)",
                }}
              ></div>
            </motion.div>
          </div>
        </main>
      </div>
      {/* </div> */}
      <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/dashboard"} />
    </>
  );
}
